import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  Check,
  Plus,
  Trash2,
  Truck,
  FileText,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import axios from 'axios';
import Base from '../Base';

import { devLog } from '../../utils/devLog';
const ShippingForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const [isDirty, setIsDirty] = useState(false);

  const [formData, setFormData] = useState({
    hero: {
      title: 'Shipping & Returns',
      subtitle: '',
      description: ''
    },
    sections: []
  });

  const updateFormData = (updater) => {
    setFormData(updater);
    setIsDirty(true);
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/shipping`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });

      // Handle different response structures
      let content = null;
      if (response.data.content) {
        content = response.data.content;
      } else if (response.data.data?.content) {
        content = response.data.data.content;
      }

      if (content) {
        setFormData({
          hero: {
            title: content.hero?.title || 'Shipping & Returns',
            subtitle: content.hero?.subtitle || '',
            description: content.hero?.description || ''
          },
          sections: content.sections || []
        });
      }
    } catch (error) {
      devLog.error('Error loading shipping page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    // Basic validation on publish
    if (!isDraft) {
      if (!formData.hero?.title?.trim()) {
        setErrorMessage('Please enter a title before publishing.');
        setShowErrorModal(true);
        return;
      }
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/template/${templateId}/page/shipping/save`, {
        status: isDraft ? 'draft' : 'complete',
        content: formData
      }, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      setIsDirty(false);
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      if (error.response?.status === 401) { navigate('/login'); return; }
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    // Preview functionality placeholder
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [
        ...(prev.sections || []),
        { title: '', content: '' }
      ]
    }));
    setIsDirty(true);
  };

  const updateSection = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: (prev.sections || []).map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
    setIsDirty(true);
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: (prev.sections || []).filter((_, i) => i !== index)
    }));
    setIsDirty(true);
  };

  const moveSection = (index, direction) => {
    const newSections = [...(formData.sections || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newSections.length) return;

    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];

    setFormData(prev => ({
      ...prev,
      sections: newSections
    }));
    setIsDirty(true);
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your shipping & returns page has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shipping information...</p>
        </div>
      </div>
    } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Shipping & Returns</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-500">
                    Last saved {lastSaved.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[#D45614] text-white rounded-lg hover:bg-[#D45614]-dark transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save & Complete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Hero Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D45614]-light rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Hero Section</h2>
                <p className="text-sm text-gray-600">The header section of your shipping page</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.hero.title}
                  onChange={(e) => updateFormData(prev => ({ ...prev, hero: { ...prev.hero, title: e.target.value } }))}
                  placeholder="Shipping & Returns"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.hero.subtitle}
                  onChange={(e) => updateFormData(prev => ({ ...prev, hero: { ...prev.hero, subtitle: e.target.value } }))}
                  placeholder="Everything you need to know"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Introduction (Optional)
                </label>
                <textarea
                  value={formData.hero.description}
                  onChange={(e) => updateFormData(prev => ({ ...prev, hero: { ...prev.hero, description: e.target.value } }))}
                  placeholder="Learn about our shipping policies and return process."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#D45614]-light rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Content Sections</h2>
                  <p className="text-sm text-gray-600">Add sections to explain your shipping and return policies</p>
                </div>
              </div>
              <button
                onClick={addSection}
                className="flex items-center gap-2 px-4 py-2 bg-[#D45614]-light text-primary rounded-lg hover:bg-[#D45614] hover:text-white transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Section
              </button>
            </div>

            {(!formData.sections || formData.sections.length === 0) ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No sections added yet</p>
                <button
                  onClick={addSection}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#D45614] text-white rounded-lg hover:bg-[#D45614]-dark transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add First Section
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.sections.map((section, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg border-2 border-gray-100 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">
                        Section {index + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => moveSection(index, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => moveSection(index, 'down')}
                          disabled={index === (formData.sections || []).length - 1}
                          className="p-1 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => removeSection(index)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete section"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Section Title *
                      </label>
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateSection(index, 'title', e.target.value)}
                        placeholder="Shipping Methods"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content *
                      </label>
                      <textarea
                        value={section.content}
                        onChange={(e) => updateSection(index, 'content', e.target.value)}
                        placeholder="We offer multiple shipping options including standard (5-7 days), express (2-3 days), and overnight delivery. Free shipping is available on orders over $100..."
                        rows={6}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Write a detailed explanation for this section
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Add Common Sections */}
          {(!formData.sections || formData.sections.length === 0) && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 className="font-semibold text-blue-900 mb-3">Quick Add Common Sections</h3>
              <p className="text-sm text-blue-700 mb-4">
                Add commonly used shipping & returns sections with one click:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Shipping Methods',
                  'Shipping Costs',
                  'Processing Time',
                  'International Shipping',
                  'Return Policy',
                  'Exchange Policy',
                  'Return Process',
                  'Refund Timeline'
                ].map((title) => (
                  <button
                    key={title}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        sections: [...prev.sections, { title, content: '' }]
                      }));
                      setIsDirty(true);
                    }}
                    className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors text-sm text-left"
                  >
                    + {title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Shipping & Returns Tips
            </h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Clearly state shipping costs and timeframes</li>
              <li>• Specify the return window (e.g., 30 days)</li>
              <li>• Explain the condition items must be in for returns</li>
              <li>• Detail who pays for return shipping</li>
              <li>• Include information about exchanges</li>
              <li>• Mention any non-returnable items</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default ShippingForm;
